/*
 * Things that open over the page: Modal (a popup you can drag by its head on a computer, a bottom sheet on a phone),
 * Menu ("More" and pickers) and Toast (a short note after an action, with Undo where it helps, or a spinner while a long
 * job runs).
 */
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Icon } from "./Icon";

export function Modal({
  title,
  open,
  onClose,
  children,
  footer,
  size,
}: {
  title: string;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** large: room for a list (Books report lines). full: the whole screen on a phone, X at the left and the main button
   * across the bottom, like Jobber's Team and Edit schedule (owner shots, 2026-10-30); a normal box on a computer. */
  size?: "large" | "full";
}) {
  const box = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const first = box.current?.querySelector<HTMLElement>("input, button:not(.iconbtn), [tabindex]");
    first?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useLayoutEffect(() => {
    if (open && box.current && !pos) {
      const r = box.current.getBoundingClientRect();
      setPos({
        x: Math.round((window.innerWidth - r.width) / 2),
        y: Math.max(32, Math.round((window.innerHeight - r.height) / 3)),
      });
    }
    if (!open) setPos(null);
  }, [open, pos]);

  const startDrag = (e: React.PointerEvent) => {
    if (!pos || (e.target as HTMLElement).closest("button")) return;
    const start = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    const move = (ev: PointerEvent) => {
      const w = box.current?.offsetWidth ?? 0;
      setPos({
        x: Math.min(Math.max(ev.clientX - start.x, 8 - w + 120), window.innerWidth - 120),
        y: Math.min(Math.max(ev.clientY - start.y, 8), window.innerHeight - 60),
      });
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  if (!open) return null;
  return createPortal(
    <>
      <div className="scrim" onClick={onClose} />
      <div
        ref={box}
        className={size ? `modal ${size}` : "modal"}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={pos ? { left: pos.x, top: pos.y } : { left: "50%", top: "15vh", transform: "translateX(-50%)" }}
      >
        <div className="modal-head" onPointerDown={startDrag}>
          <h2>{title}</h2>
          <button type="button" className="iconbtn" onClick={onClose} aria-label="Close">
            <Icon name="close" />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer ? <div className="modal-foot">{footer}</div> : null}
      </div>
    </>,
    document.body,
  );
}

/** A dropdown that opens under its button and closes on a click outside or Escape. */
export function Menu({
  button,
  children,
  align = "right",
}: {
  button: (p: { open: boolean; toggle: () => void }) => ReactNode;
  children: (close: () => void) => ReactNode;
  align?: "left" | "right";
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("pointerdown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return (
    <div ref={wrap} style={{ position: "relative" }}>
      {button({ open, toggle: () => setOpen((o) => !o) })}
      {open ? (
        <div className="menu" role="menu" style={{ [align]: 0, top: "calc(100% + 6px)" }}>
          {children(close)}
        </div>
      ) : null}
    </div>
  );
}

/**
 * A list filter as one pill, "Status | All", that opens its choices (Jobber's and the old Steward's). Each choice can carry
 * a count. Shows as pressed while anything but the first choice is picked.
 */
export function FilterPick<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string; count?: number }[];
}) {
  const picked = options.find((o) => o.value === value) ?? options[0];
  return (
    <Menu
      align="left"
      button={({ open, toggle }) => (
        <button
          type="button"
          className="filter-pick"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-pressed={value !== options[0]?.value}
          onClick={toggle}
        >
          <span className="muted">{label}</span>
          <span className="filter-pick-value">{picked?.label}</span>
          <Icon name="chevronDown" size={16} />
        </button>
      )}
    >
      {(close) =>
        options.map((o) => (
          <button
            key={o.value}
            type="button"
            role="menuitemradio"
            aria-checked={o.value === value}
            aria-current={o.value === value}
            onClick={() => {
              close();
              onChange(o.value);
            }}
          >
            <span>{o.label}</span>
            {o.count !== undefined ? <span className="muted num">{o.count}</span> : null}
          </button>
        ))
      }
    </Menu>
  );
}

interface ToastItem {
  id: number;
  text: string;
  kind: "info" | "error";
  undo?: () => void;
  life: number;
  leaving?: boolean;
  /** A long job still running (an export): a spinner, no timer, until its finish() is called. */
  busy?: boolean;
}

interface ToastOpts {
  kind?: "info" | "error";
  undo?: () => void;
  busy?: boolean;
}
/** Changes a busy toast to its result ("Export ready") and starts its normal timer. */
type Finish = (text: string, opts?: { kind?: "info" | "error" }) => void;

/** How long the slide-out takes before a toast is removed (matches --timing-base). */
const TOAST_EXIT_MS = 200;
const ToastContext = createContext<(text: string, opts?: ToastOpts) => Finish>(() => () => undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const expire = useCallback((id: number, life: number) => {
    setTimeout(() => setItems((xs) => xs.map((x) => (x.id === id ? { ...x, leaving: true } : x))), life - TOAST_EXIT_MS);
    setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), life);
  }, []);
  const show = useCallback(
    (text: string, opts: ToastOpts = {}): Finish => {
      const id = Date.now() + Math.random();
      const life = opts.undo ? 8000 : 4000;
      setItems((xs) => [
        ...xs.slice(-2),
        {
          id,
          text,
          kind: opts.kind ?? "info",
          life,
          ...(opts.undo ? { undo: opts.undo } : {}),
          ...(opts.busy ? { busy: true } : {}),
        },
      ]);
      if (!opts.busy) {
        expire(id, life);
        return () => undefined;
      }
      return (done, o = {}) => {
        setItems((xs) => xs.map((x) => (x.id === id ? { ...x, text: done, kind: o.kind ?? "info", busy: false } : x)));
        expire(id, 4000);
      };
    },
    [expire],
  );
  return (
    <ToastContext.Provider value={show}>
      {children}
      <div className="toasts" aria-live="polite">
        {items.map((t) => (
          <div
            key={t.id}
            className={`toast${t.kind === "error" ? " error" : ""}${t.leaving ? " leaving" : ""}`}
            style={{ ["--toast-life" as string]: `${String(t.life)}ms` }}
          >
            {t.busy ? <span className="spin" aria-hidden /> : null}
            {t.text}
            {t.undo ? (
              <button
                type="button"
                className="btn tertiary small"
                style={{ color: "inherit" }}
                onClick={() => {
                  t.undo?.();
                  setItems((xs) => xs.filter((x) => x.id !== t.id));
                }}
              >
                Undo
              </button>
            ) : null}
            {t.undo ? <span className="toast-timer" aria-hidden /> : null}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);
